import React from "react";

export type LogoVariant = "full" | "mark";
export type LogoTone = "light" | "dark";

export type LogoProps = {
  /**
   * Visual variant:
   * - `full`: default horizontal lockup
   * - `mark`: square-ish mark (falls back to same asset for now)
   */
  variant?: LogoVariant;
  /**
   * Helps pick the right rendering behavior on different surfaces.
   * Current asset is an SVG; we don't recolor it to follow BRAND rules.
   */
  tone?: LogoTone;
  /**
   * Height in px. Width auto to preserve aspect ratio.
   */
  height?: number;
  className?: string;
  /**
   * Accessible label. If omitted, defaults to "KamuKirim - Payroll".
   * If you want the logo to be decorative, pass `alt=""`.
   */
  alt?: string;
  /**
   * If true, prevents the image from being dragged.
   */
  draggable?: boolean;
};

const DEFAULT_ALT = "KamuKirim - Payroll";

/**
 * Branded Logo component.
 *
 * Branding rules:
 * - Do not recolor, rotate, stretch, add shadows/gradients/outline.
 * - Keep clearspace around it in layout (handled by parent).
 *
 * Implementation notes:
 * - Uses the transparent PNG in `src/assets/app-logo.png` for now.
 * - If you later add variant-specific assets, we can switch based on `variant`.
 */
export function Logo({
  variant = "full",
  tone = "light",
  height = 40,
  className,
  alt = DEFAULT_ALT,
  draggable = false,
}: LogoProps) {
  // Keep import local to avoid circular dependencies.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const logoUrl = new URL(
    "../../assets/app-logo.png",
    import.meta.url,
  ).toString();

  // We intentionally do not apply CSS filters or recolor.
  // The `tone` prop exists for future-proofing, in case we add a dark/light asset.
  const toneClass = tone === "dark" ? "opacity-95" : "opacity-100";

  const variantClass = variant === "mark" ? "rounded-md" : "";

  return (
    <img
      src={logoUrl}
      alt={alt}
      draggable={draggable}
      className={[
        "block w-auto select-none",
        toneClass,
        variantClass,
        className ?? "",
      ].join(" ")}
      style={{ height }}
    />
  );
}
